/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { RecommendationBanner } from '../recommendation-banner/RecommendationBanner';
import type { RecommendationBannerVariant } from '../recommendation-banner/types';

import messages from './messages';

const styles = cssMap({
	iconTile: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
		paddingInlineStart: token('space.025'),
		paddingInlineEnd: token('space.025'),
		flexShrink: 0,
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	iconScaler: {
		display: 'inline-flex',
		transformOrigin: 'center',
		transform: 'scale(1.5)',
	},
});

const ICON_TILE = (
	<Box xcss={styles.iconTile} aria-hidden="true" testId="smart-context-links-banner-icon-tile">
		<Box xcss={styles.iconScaler}>
			<LinkIcon label="" color={token('color.icon')} />
		</Box>
	</Box>
);

export type SmartContextLinksBannerProps = {
	/**
	 * Number of recommended sources. When `0` the banner renders nothing so
	 * callers can pass the raw value from a recommendations hook without
	 * guarding on it themselves.
	 */
	recommendationCount: number;
	/** Invoked when the user presses the "Add sources" CTA. */
	onAddLinks: () => void;
	/** Invoked when the user dismisses the banner. Omit to render a non-dismissible banner. */
	onDismiss?: () => void;
	/** Layout variant passed through to {@link RecommendationBanner}. */
	variant?: RecommendationBannerVariant;
	/** Override for the root testId. Defaults to `'smart-context-links-banner'`. */
	testId?: string;
};

const SmartContextLinksBannerComponent = ({
	recommendationCount,
	onAddLinks,
	onDismiss,
	variant = 'inline',
	testId = 'smart-context-links-banner',
}: SmartContextLinksBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (recommendationCount <= 0) {
		return null;
	}

	const isStacked = variant === 'stacked';
	const dismissProps = onDismiss
		? {
				onDismiss,
				dismissLabel: formatMessage(
					isStacked ? messages.dismissButtonLabel : messages.dismissLabel,
				),
			}
		: {};

	return (
		<RecommendationBanner
			testId={testId}
			icon={ICON_TILE}
			title={formatMessage(isStacked ? messages.sidebarTitle : messages.title)}
			description={formatMessage(isStacked ? messages.sidebarDescription : messages.description, {
				count: recommendationCount,
			})}
			primaryActionLabel={formatMessage(messages.addLinksCta)}
			onPrimaryAction={onAddLinks}
			variant={variant}
			{...dismissProps}
		/>
	);
};

export const SmartContextLinksBanner: React.MemoExoticComponent<
	(props: SmartContextLinksBannerProps) => React.JSX.Element | null
> = memo(SmartContextLinksBannerComponent);
